import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Users, Truck, Zap, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const categories = [
  { slug: "notebooks", name: "Notebooks & Paper", color: "from-indigo-500 to-blue-500", img: "https://images.unsplash.com/photo-1601001435957-74f0958a93fb?crop=entropy&cs=srgb&fm=jpg&w=500" },
  { slug: "writing", name: "Writing Instruments", color: "from-coral-500 to-pink-500", img: "https://images.unsplash.com/photo-1609881532802-493ca789f868?crop=entropy&cs=srgb&fm=jpg&w=500" },
  { slug: "art", name: "Art Supplies", color: "from-amber-500 to-orange-500", img: "https://images.unsplash.com/photo-1568149537277-4daf47b8a6dd?crop=entropy&cs=srgb&fm=jpg&w=500" },
  { slug: "backpacks", name: "Backpacks & Bags", color: "from-emerald-500 to-teal-500", img: "https://images.unsplash.com/photo-1535982330050-f1c2fb79ff78?crop=entropy&cs=srgb&fm=jpg&w=500" },
  { slug: "classroom", name: "Classroom Essentials", color: "from-purple-500 to-fuchsia-500", img: "https://images.unsplash.com/photo-1615988938302-bd2a5a7023bc?crop=entropy&cs=srgb&fm=jpg&w=500" },
  { slug: "tech", name: "Tech & Accessories", color: "from-cyan-500 to-sky-500", img: "https://images.unsplash.com/photo-1726726192148-af52008ff663?crop=entropy&cs=srgb&fm=jpg&w=500" },
];

export const Home = () => {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 -left-24 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl" />
          <div className="absolute top-40 right-0 w-96 h-96 bg-amber-200/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-indigo-100 rounded-full px-3 py-1 text-xs font-semibold text-indigo-700 mb-6">
              <Zap className="w-3 h-3" /> Auto-fulfilled by our wholesaler in real time
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
              Everything your <span className="text-indigo-600">classroom</span> & <span className="text-coral-500" style={{ color: "#FF6B6B" }}>kids</span> need.
              <br />Delivered on autopilot.
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-lg">
              A smarter stationery store built for parents and teachers. Order once — our automated wholesaler network packs, ships, and delivers while you get on with your day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop">
                <Button size="lg" className="rounded-full bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/30" data-testid="hero-explore-supplies-btn">
                  Explore supplies <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/wholesale">
                <Button size="lg" variant="outline" className="rounded-full border-slate-300" data-testid="hero-teacher-wholesale-btn">
                  <GraduationCap className="w-4 h-4 mr-1" /> Teacher / School wholesale
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-indigo-600" /> Auto-fulfilled orders</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-coral-500" style={{ color: "#FF6B6B" }} /> 12k+ parents & teachers</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Stripe-secured checkout</div>
            </div>
          </div>
          <div className="grid grid-cols-6 gap-3 h-[520px]">
            <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1726726192148-af52008ff663?crop=entropy&cs=srgb&fm=jpg&w=900" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 rounded-3xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1601001435957-74f0958a93fb?crop=entropy&cs=srgb&fm=jpg&w=600" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 rounded-3xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1568149537277-4daf47b8a6dd?crop=entropy&cs=srgb&fm=jpg&w=600" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-3 rounded-3xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1609881532802-493ca789f868?crop=entropy&cs=srgb&fm=jpg&w=600" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-3 rounded-3xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1535982330050-f1c2fb79ff78?crop=entropy&cs=srgb&fm=jpg&w=600" alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-2">Shop by category</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Find exactly what you need</h2>
          </div>
          <Link to="/shop" className="text-sm font-semibold text-indigo-600 hover:underline hidden sm:inline">Browse all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map((c) => (
            <Link key={c.slug} to={`/shop?category=${c.slug}`} data-testid={`category-card-${c.slug}`}
                  className="group relative rounded-2xl overflow-hidden aspect-[4/3] card-lift">
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className={`absolute inset-0 bg-gradient-to-br ${c.color} opacity-70 mix-blend-multiply`} />
              <div className="absolute inset-0 flex items-end p-5">
                <div>
                  <div className="text-white font-display font-extrabold text-xl drop-shadow">{c.name}</div>
                  <div className="text-white/90 text-sm mt-1 flex items-center gap-1 font-semibold">Shop now <ArrowRight className="w-3 h-3" /></div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-coral-500 mb-2" style={{ color: "#FF6B6B" }}>Bestsellers</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Loved by parents & teachers</h2>
          </div>
          <Link to="/shop" className="text-sm font-semibold text-indigo-600 hover:underline">See all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-2">The SupplySprout advantage</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Order in one tap. Auto-fulfilled in real time.</h2>
          <p className="mt-3 text-slate-600">Every order flows into our wholesaler network the moment you check out. Watch it happen live from your order history.</p>
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            { n: "01", t: "You order", d: "Add to cart, check out with Stripe — parent or teacher account." },
            { n: "02", t: "Wholesaler receives", d: "Our automated wholesaler API accepts the order instantly." },
            { n: "03", t: "Packed & shipped", d: "Stages auto-advance from packed → shipped as fulfillment happens." },
            { n: "04", t: "Delivered", d: "Your order arrives. You track every step live in the app." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl bg-white border border-slate-200 p-6 card-lift">
              <div className="font-mono text-sm text-indigo-600 font-bold">{s.n}</div>
              <div className="font-display font-bold text-lg mt-1">{s.t}</div>
              <div className="text-sm text-slate-600 mt-2">{s.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
